import { sparkToast } from '@spark';
import { data_from_server_get, data_from_server_post } from '@/shared/http/client';
import { TaskType } from '@/shared/domain/taskTypes';
import { getUserFacingDatasetName } from '@/features/datasets/utils/datasetCloneNames';
import { NO_PREDICTIONS_GENERATION_TARGET } from '@/features/datasets/components/DataCollection/GenerateDataWithAI/useGenerationTargets';

/** Task types with a "Generate data with AI" modal flow. */
export function isAIGenerationDatasetSupported({ taskType }) {
    return [
        TaskType.classification,
        TaskType.NER,
        TaskType.summarization,
        TaskType.contentGeneration,
    ].includes(taskType);
}

const trimOrNull = (text) => {
    const trimmed = (text || '').trim();
    return trimmed || null;
};

/** UI language field is a multi-select array; older single-string values still map. */
const toLanguages = (source) => {
    if (Array.isArray(source?.languages) && source.languages.length) return source.languages;
    if (source?.language) return [source.language];
    return null;
};

const trimmedExampleTexts = (examples) =>
    (examples || []).map((text) => (text || '').trim()).filter(Boolean);

function labeledGenerationKind(taskType) {
    if (taskType === TaskType.classification) return 'classification';
    if (taskType === TaskType.NER) return 'ner';
    return null;
}

/** Per-label example strings → wire ``GenerationExampleItem`` rows. */
const flattenTargetExamples = (targetConfigs, isMultilabel) => {
    const items = [];
    Object.entries(targetConfigs || {}).forEach(([name, config]) => {
        if (isMultilabel && name === NO_PREDICTIONS_GENERATION_TARGET) return;
        trimmedExampleTexts(config?.examples).forEach((text) => {
            items.push(
                isMultilabel
                    ? { kind: 'multi', text, response: { kind: 'multi', labels: [name] } }
                    : { kind: 'single', text, response: { kind: 'single', label: name } }
            );
        });
    });
    return items;
};

/**
 * UI per-target config → wire `TargetGenerationConfig`. Advanced fields
 * (text length / language / tone) are only sent when the user opened the
 * Advanced Settings toggle for that target; otherwise the Figma defaults
 * would silently over-constrain every label. Returns null when empty.
 */
const toWireTargetConfig = (config) => {
    if (!config) return null;
    const examples = trimmedExampleTexts(config.examples);
    const wire = {
        description: trimOrNull(config.description),
        examples_prompt: examples.length ? null : trimOrNull(config.examplesPrompt),
        examples: examples.length ? examples : null,
        exclude: trimOrNull(config.exclude),
    };
    if (config.advancedOpen) {
        wire.text_length = config.textLength || null;
        wire.languages = toLanguages(config);
        wire.tone_styles = config.toneStyles?.length ? config.toneStyles : null;
    }
    const cleaned = Object.fromEntries(Object.entries(wire).filter(([, value]) => value != null));
    return Object.keys(cleaned).length ? cleaned : null;
};

/** UI Advanced settings state → wire `GenerationSettings`. Returns null when empty.
 * `custom_guidelines` is intentionally not sent: the Define Task "Custom
 * Guidelines (dataset context)" field maps to the top-level `description`. */
const toWireGenerationSettings = (settings) => {
    if (!settings) return null;
    const wire = {
        domain: trimOrNull(settings.domain),
        languages: toLanguages(settings),
        tone_styles: settings.toneStyles?.length ? settings.toneStyles : null,
        text_length: settings.textLength || null,
        content_type: settings.contentType || null,
        exclude: trimOrNull(settings.exclude),
        examples_prompt: trimOrNull(settings.examplesPrompt),
    };
    const cleaned = Object.fromEntries(Object.entries(wire).filter(([, value]) => value != null));
    return Object.keys(cleaned).length ? cleaned : null;
};

/**
 * Modal flow payload → `POST ai-generation/generate-dataset` request body.
 *
 * @param {Object} args
 * @param {Object} args.usecase Active usecase (client_id / _id).
 * @param {Object} args.payload Flow `onGenerate` payload —
 *   `{ goal, description, examples?, exclude?, totalRecords?, recordCounts?,
 *      targetConfigs?, settings }`.
 */
export function buildGenerateDatasetRequest({ usecase, payload }) {
    const settings = { ...(payload.settings || {}) };
    // Flow-level Exclude (multi-label step 1) rides on the global settings.
    if (payload.exclude) settings.exclude = payload.exclude;

    const recordCounts = Object.fromEntries(
        Object.entries(payload.recordCounts || {}).filter(([, count]) => Number.isFinite(count))
    );
    const targetConfigs = Object.fromEntries(
        Object.entries(payload.targetConfigs || {})
            .map(([name, config]) => [name, toWireTargetConfig(config)])
            .filter(([, config]) => config != null)
    );
    const examplesFromTargets = flattenTargetExamples(payload.targetConfigs, usecase?.is_multilabel);
    const labeledKind = labeledGenerationKind(usecase?.task_type);
    const taskSpecificArgs = labeledKind && (Object.keys(recordCounts).length || Object.keys(targetConfigs).length)
        ? {
            kind: labeledKind,
            ...(Object.keys(recordCounts).length ? { record_counts: recordCounts } : {}),
            ...(Object.keys(targetConfigs).length ? { target_configs: targetConfigs } : {}),
        }
        : undefined;

    return {
        client_id: usecase.client_id,
        usecase_id: usecase._id,
        dataset_name: trimOrNull(payload.datasetName) || undefined,
        goal: payload.goal,
        description: payload.description,
        examples: payload.examples?.length ? payload.examples : examplesFromTargets.length ? examplesFromTargets : undefined,
        dataset_size: Number.isFinite(payload.totalRecords) ? payload.totalRecords : undefined,
        task_specific_args: taskSpecificArgs,
        generation_settings: toWireGenerationSettings(settings) || undefined,
    };
}

/**
 * Single source of truth for kicking off AI generation from a UI action.
 * Wraps `startAIGenerationDataset` with the shared "task not loaded" guard,
 * success/failure toasts, and error rewrapping so callers (the flow
 * `onGenerate` handlers) only have to plug in their own post-success side
 * effects (closing the modal, refreshing usecases, advancing steps, etc.).
 */
export function runAIGenerationDataset({ usecase, payload, seedGenerationProgress }) {
    if (!usecase) {
        sparkToast.error('Task not loaded yet');
        return Promise.reject(new Error('Task not loaded yet'));
    }
    return startAIGenerationDataset({ usecase, payload })
        .then((res) => {
            seedGenerationProgress?.({
                usecaseId: usecase._id,
                datasetName: res?.dataset_name,
                status: res?.status || 'queued',
                jobsAhead: res?.jobs_ahead,
            });
            sparkToast.success(
                `AI generation started. Dataset: ${getUserFacingDatasetName(res?.dataset_name) || 'queued'}`
            );
            return res;
        })
        .catch((err) => {
            sparkToast.error(`Failed to start AI generation: ${err?.message || err}`);
            return Promise.reject(err);
        });
}

/** Past Generate-with-AI wizard configs for the load-previous dropdown; empty list on failure. */
export async function fetchAIGenerationSessions(usecaseId) {
    if (!usecaseId) return [];
    try {
        let sessions = [];
        await data_from_server_get(`ai-generation/sessions/${usecaseId}`, (res) => {
            sessions = Array.isArray(res?.sessions) ? res.sessions : [];
        });
        return sessions;
    } catch {
        return [];
    }
}

/**
 * Default (next free ``{task}_ai{n}``) dataset name plus taken names for the
 * Dataset Name field's inline uniqueness validation.
 */
export async function fetchGenerationDatasetNameSuggestion(usecaseId) {
    if (!usecaseId) return { defaultName: '', existingNames: [] };
    try {
        let suggestion = { defaultName: '', existingNames: [] };
        await data_from_server_get(`ai-generation/dataset-name-suggestion/${usecaseId}`, (res) => {
            suggestion = {
                defaultName: res?.default_name || '',
                existingNames: Array.isArray(res?.existing_names) ? res.existing_names : [],
            };
        });
        return suggestion;
    } catch {
        return { defaultName: '', existingNames: [] };
    }
}

export function startAIGenerationDataset({ usecase, payload }) {
    if (!usecase?._id || !usecase?.client_id) {
        return Promise.reject(new Error('Missing task identifiers'));
    }

    const taskType = usecase?.task_type;
    if (!isAIGenerationDatasetSupported({ taskType })) {
        return Promise.reject(new Error('AI generation is not supported for this task type.'));
    }

    return new Promise((resolve, reject) => {
        data_from_server_post(
            'ai-generation/generate-dataset',
            buildGenerateDatasetRequest({ usecase, payload }),
            (res) => resolve(res),
            (err) => reject(new Error(err))
        );
    });
}
